import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Cookie, Shield, BarChart2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { SEOHead } from '@/components/SEOHead';

const STORAGE_KEY = 'dsh_cookie_consent';

const cookieCategories = [
  {
    id: 'essential',
    icon: Shield,
    title: 'Essential Cookies',
    description:
      'These cookies are required for the website to function correctly. They enable core features like navigation and theme preferences. They cannot be disabled.',
    always: true,
    examples: ['Theme preference (dark/light mode)', 'Cookie consent status'],
  },
  {
    id: 'analytics',
    icon: BarChart2,
    title: 'Analytics Cookies',
    description:
      'These cookies help us understand how visitors interact with the website. All data is anonymized. Currently not in use — no analytics service is active.',
    always: false,
    examples: ['Page views', 'Session duration', 'Navigation paths'],
  },
];

export default function CookieSettingsPage() {
  const [consent, setConsent] = useState(null);
  const [analytics, setAnalytics] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    setConsent(stored);
    setAnalytics(stored === 'accepted');
  }, []);

  function save() {
    const value = analytics ? 'accepted' : 'declined';
    localStorage.setItem(STORAGE_KEY, value);
    setConsent(value);
    setSaved(true);
    toast.success('Cookie preferences saved.');
    setTimeout(() => setSaved(false), 3000);
  }

  function acceptAll() {
    setAnalytics(true);
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setConsent('accepted');
    toast.success('All cookies accepted.');
  }

  function declineAll() {
    setAnalytics(false);
    localStorage.setItem(STORAGE_KEY, 'declined');
    setConsent('declined');
    toast.success('Non-essential cookies declined.');
  }

  const statusLabel = consent === 'accepted' ? 'Accepted' : consent === 'declined' ? 'Declined' : 'Not set';
  const statusColor = consent === 'accepted' ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-muted text-muted-foreground border-border';

  return (
    <Layout>
      <SEOHead
        title="Cookie Settings"
        description="Manage your cookie preferences for DeinServerHost."
        canonical="/cookie-settings"
        noindex={true}
      />
      <div className="section-spacing">
        <div className="container-main max-w-2xl">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-black">Cookie Settings</h1>
                <p className="text-sm text-muted-foreground">Manage your privacy preferences</p>
              </div>
            </div>

            {/* Current status */}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${statusColor}`}>
              {consent === 'accepted' ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
              Current status: <span className="font-bold">{statusLabel}</span>
            </div>
          </motion.div>

          {/* Categories */}
          <div className="space-y-4 mb-8">
            {cookieCategories.map((cat, i) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="glass rounded-2xl border border-border p-6"
                data-testid={`cookie-category-${cat.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <cat.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-sm">{cat.title}</h3>
                        {cat.always && (
                          <Badge variant="secondary" className="text-xs">Always active</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                        {cat.description}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {cat.examples.map((ex) => (
                          <span key={ex} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                            {ex}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Toggle */}
                  <button
                    disabled={cat.always}
                    onClick={() => !cat.always && setAnalytics((v) => !v)}
                    className={`relative w-11 h-6 rounded-full transition-colors shrink-0 mt-0.5 ${
                      cat.always
                        ? 'bg-primary cursor-not-allowed opacity-60'
                        : analytics
                        ? 'bg-primary cursor-pointer'
                        : 'bg-muted cursor-pointer'
                    }`}
                    aria-label={`Toggle ${cat.title}`}
                    data-testid={`toggle-${cat.id}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        cat.always || analytics ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <Button
              variant="outline"
              onClick={declineAll}
              className="flex-1 border-border hover:border-primary/30"
              data-testid="decline-all-btn"
            >
              Decline All
            </Button>
            <Button
              variant="outline"
              onClick={acceptAll}
              className="flex-1 border-border hover:border-primary/30"
              data-testid="accept-all-btn"
            >
              Accept All
            </Button>
            <Button
              onClick={save}
              className="flex-1 bg-primary hover:bg-primary/90 glow-hover font-bold"
              data-testid="save-preferences-btn"
            >
              {saved ? <><Check className="w-4 h-4 mr-2" />Saved</> : 'Save Preferences'}
            </Button>
          </motion.div>

          {/* Info note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="text-xs text-muted-foreground text-center mt-6"
          >
            Your preferences are stored locally in your browser and can be changed at any time.
          </motion.p>

        </div>
      </div>
    </Layout>
  );
}
